import { Alert, Descriptions, Form } from 'antd'
import { formatScope } from 'features/libra/inspectionPlanning/scopeMeasurement'
import { GeckoMeasurementSystem } from 'types'

import { ExecutionColumn, ExecutionRow, ExecutionValue } from './executionTypes'
import { ExecutionValueInput } from './ExecutionValueInput'
import { isInspectionResultValueValid } from './inspectionResultsModel'

export const ExecutionRowEditor = ({
  row,
  columns,
  system,
  sourceZone,
  linked,
  basisChanged,
  readonly,
  onChange,
}: {
  row: ExecutionRow
  columns: ExecutionColumn[]
  system: GeckoMeasurementSystem
  sourceZone: Record<string, unknown>
  basisChanged: boolean
  linked: boolean
  readonly: boolean
  onChange: (field: string, value: ExecutionValue) => void
}) => {
  const { method, requiredCoveragePercent } = row.basis
  const invalid = columns.filter(
    (column) =>
      !isInspectionResultValueValid(column, row.values[column.field], method)
  )
  return (
    <>
      {!linked && (
        <Alert
          type='warning'
          title='The planned zone, area or method is no longer available. This row retains its recorded basis. Review it before completing the workflow.'
        />
      )}
      {basisChanged && (
        <Alert
          type='warning'
          title='Planning details have changed. This row retains the procedure and estimate used when it was added.'
        />
      )}
      <Descriptions
        column={1}
        size='small'
        items={[
          {
            key: 'coverage',
            label: 'Required coverage',
            children:
              requiredCoveragePercent === undefined
                ? 'Not specified'
                : `${requiredCoveragePercent}%`,
          },
          {
            key: 'procedure',
            label: 'Procedure / specification',
            children: method.procedure || 'Not specified',
          },
          {
            key: 'instructions',
            label: 'Location and scanning instructions',
            children: method.instructions || 'Not specified',
          },
          {
            key: 'estimate',
            label: 'Estimated scope',
            children: method.scopeType
              ? formatScope(method.estimatedScope, method.scopeType, system)
              : 'Not specified',
          },
        ]}
      />
      <Form layout='vertical' component='div'>
        {columns.map((column) => (
          <ExecutionValueInput
            key={column.field}
            column={column}
            value={row.values[column.field]}
            method={method}
            system={system}
            sourceZone={sourceZone}
            readonly={readonly}
            onChange={(value) => onChange(column.field, value)}
          />
        ))}
      </Form>
      {!readonly && invalid.length > 0 && (
        <Alert
          type='warning'
          title={`Check: ${invalid.map((column) => column.label).join(', ')}`}
        />
      )}
    </>
  )
}
